import {classes, present} from '@bitstillery/common/lib/utils'
import {unique_id} from '@bitstillery/common/lib/utils'
import {Button} from '@bitstillery/common/components/ui/button/button'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {MithrilComponent} from '@bitstillery/mithril'
import {state} from '@bitstillery/mithril'
import {modelref_adapter, modelref_assign} from '@bitstillery/common/lib/store'
import {event_value, html_element} from '@bitstillery/common/lib/dom'
import {logger} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'

import type {FieldTextAttrs, FieldTextSuggestion} from '@bitstillery/common/types/field'
import {failure_message} from '@bitstillery/common/lib/validation'

// Navigation keys are keys that change the selection of the suggestion dropdown.
const NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp'])

interface FieldTextData {
    focussed: boolean
    password: {
        hide: boolean
    }
    search: {
        selected: number | null
    }
}

export class FieldText<S extends FieldTextSuggestion = FieldTextSuggestion> extends MithrilComponent<FieldTextAttrs<S>> {
    $suggestion_container: HTMLElement | null = null
    data: FieldTextData = state(
        {
            focussed: false,
            password: {
                hide: true,
            },
            search: {
                selected: null,
            },
        },
        'field-text-data',
    )

    name = unique_id()
    is_locale_nl = Intl.NumberFormat().resolvedOptions().locale === 'nl'

    // No dom when view() rendered nothing for a missing model.
    override oncreate(vnode: m.Vnode<FieldTextAttrs<S>> & {dom?: HTMLElement | null}) {
        if (!vnode.dom) {
            return
        }

        if (vnode.attrs.autofocus) {
            setTimeout(() => present(vnode.dom, 'text field element').focus(), 100)
        }
        if (vnode.attrs.search) {
            this.$suggestion_container = vnode.dom.querySelector<HTMLElement>('.search-suggestions')
        }
    }

    format_value(vnode: m.Vnode<FieldTextAttrs<S>>, value: string | number | null) {
        if (vnode.attrs.type === 'date') {
            // The parsed value is formatted as "yyyy-mm-dd"
            // The displayed date format will differ from the actual value.
            if (!value) {
                return null
            }
        }

        if (vnode.attrs.type === 'number') {
            // If the value is empty, return null.
            if (value === '' || value === null) {
                return null
            }

            value = Number(value)
            if (isNaN(value)) {
                return null
            }

            // Format decimals if specified
            if (typeof vnode.attrs.decimals === 'number') {
                value = Number(value.toFixed(vnode.attrs.decimals))
            }

            // Make sure no values outside the expected boundaries are applied to the model.
            // Do not apply min, otherwise the min is applied while a user is typing.
            if (typeof vnode.attrs.max === 'number' && value > vnode.attrs.max) {
                return vnode.attrs.max
            }
        }

        return value
    }

    navigate_keyboard(vnode: m.Vnode<FieldTextAttrs<S>>, e: KeyboardEvent) {
        if (!vnode.attrs.search) {
            return
        }
        if (e.key === 'Backspace') {
            // Backspace deselects any selected suggestion
            this.data.search.selected = null
            return
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            if (vnode.attrs.search.options.length > 0) {
                const {model_ref} = modelref_adapter(vnode.attrs.model)

                // Enter pressed while a suggestion is selected.
                if (this.data.search.selected !== null) {
                    const submit_value = vnode.attrs.search.options[this.data.search.selected]
                    if (submit_value) {
                        // Update selection from the targeted suggestion.
                        modelref_assign(model_ref, submit_value.value)
                        this.onsuggestion(vnode, submit_value)
                    }
                }

                vnode.attrs.search.options.splice(0)
            }
            return
        }

        if (vnode.attrs.search.options.length === 0) {
            this.data.search.selected = null
            return
        }

        if (!NAVIGATION_KEYS.has(e.key) || ['ArrowLeft', 'ArrowRight'].includes(e.key)) {
            return
        }

        if (this.data.search.selected === null) {
            // Navigating the cursor in the input has no influence on the selected suggestion.
            this.data.search.selected = 0
        } else {
            if (e.key === 'ArrowDown') {
                if (this.data.search.selected < vnode.attrs.search.options.length - 1) {
                    this.data.search.selected += 1
                }
            } else if (e.key === 'ArrowUp') {
                if (this.data.search.selected > 0) {
                    this.data.search.selected -= 1
                } else {
                    this.data.search.selected = 0
                }
            } else if (e.key === 'PageDown') {
                if (this.data.search.selected < vnode.attrs.search.options.length - 5) {
                    this.data.search.selected += 5
                } else {
                    this.data.search.selected = vnode.attrs.search.options.length - 1
                }
            } else if (e.key === 'PageUp') {
                if (this.data.search.selected > 4) {
                    this.data.search.selected -= 5
                } else {
                    this.data.search.selected = 0
                }
            }
        }

        // Apply the updated selection to the DOM; scroll to the selected option.
        let selected_option = html_element(this.$suggestion_container?.children[this.data.search.selected])
        if (!selected_option && this.$suggestion_container?.children.length) {
            selected_option = html_element(this.$suggestion_container.children[0])
        }

        if (selected_option) {
            const suggestion_container = present(this.$suggestion_container, 'suggestion container')
            suggestion_container.scrollTo({
                behavior: 'smooth',
                top: selected_option.offsetTop - suggestion_container.clientHeight / 2 + selected_option.clientHeight / 2,
            })
        }
    }

    onsuggestion(vnode: m.Vnode<FieldTextAttrs<S>>, suggestion: S | undefined) {
        if (!vnode.attrs.search || !suggestion) {
            return
        }

        logger.debug(`[FieldText] exact suggestion match: ${suggestion.value}`)
        if (vnode.attrs.search.onsuggestion) {
            vnode.attrs.search.onsuggestion(suggestion)
        }
    }

    view(vnode: m.Vnode<FieldTextAttrs<S>>) {
        const {model_value, model_ref} = modelref_adapter(vnode.attrs.model)
        if (model_ref === null) {
            return
        }
        const {validation} = vnode.attrs

        if (vnode.attrs.validation && model_value) {
            vnode.attrs.validation.dirty = true
        }

        // Add human readable description to the validation object.
        if (validation && vnode.attrs.label) {
            validation.description = vnode.attrs.label
        }

        const invalid = validation ? validation._invalid : false
        const {disabled} = vnode.attrs

        return (
            <div
                id={`field-text-${this.name}`}
                className={classes('c-field-text', vnode.attrs.composed ? null : 'field', vnode.attrs.className, {
                    disabled: disabled,
                    invalid: validation && invalid && validation.dirty,
                    valid: validation && !invalid && validation.dirty,
                })}
            >
                {vnode.attrs.label && (
                    <label>
                        {vnode.attrs.label}
                        {vnode.attrs.icon && <Icon name={vnode.attrs.icon} />}
                        {validation && <span className='validation'>{validation.label}</span>}
                    </label>
                )}
                {(() => {
                    const search = vnode.attrs.search
                    if (search && search.linked) {
                        return [
                            <div className='search-linked'>
                                <div className='linked-label'>{model_value}</div>
                                <Button
                                    disabled={vnode.attrs.disabled}
                                    icon='close'
                                    onclick={() => {
                                        search.options.length = 0
                                        modelref_assign(model_ref, '')
                                        if (search.onreset) {
                                            search.onreset()
                                        }
                                    }}
                                    tip='Clear selection'
                                    type='info'
                                />
                            </div>,
                            (() => {
                                if (invalid && validation?.dirty) {
                                    return <div className='help validation'>{failure_message(invalid)}</div>
                                } else if (vnode.attrs.help) {
                                    return <div class='help'>{vnode.attrs.help}</div>
                                }
                                return undefined
                            })(),
                        ]
                    }

                    const ac = vnode.attrs.autocomplete
                    const autocomplete_attr = ac === undefined ? undefined : ac === true ? 'on' : ac === false ? 'off' : ac

                    return (
                        <div className='input-wrapper'>
                            <input
                                autocomplete={autocomplete_attr}
                                autofocus={vnode.attrs.autofocus}
                                disabled={vnode.attrs.disabled}
                                id={vnode.attrs.id}
                                onkeydown={(e: KeyboardEvent) => {
                                    this.navigate_keyboard(vnode, e)
                                    if (vnode.attrs.onkeydown) {
                                        vnode.attrs.onkeydown(e)
                                    }
                                    // Disable the . key in locale nl.
                                    // If this is not present, it will lead to an oninput with '' in target.value, causing a clear of the input value.
                                    if (e.key === '.' && this.is_locale_nl && vnode.attrs.type === 'number') {
                                        e.preventDefault()
                                    }
                                }}
                                onkeypress={(e: KeyboardEvent) => {
                                    // Only handles submit on Enter key.
                                    if (e.key !== 'Enter') {
                                        return
                                    }
                                    const {model_value: enter_model_value} = modelref_adapter(vnode.attrs.model)
                                    if (vnode.attrs.onenter) {
                                        vnode.attrs.onenter(e, enter_model_value)
                                    }
                                }}
                                onblur={(e) => {
                                    const {value} = e.currentTarget

                                    if (vnode.attrs.onfocusout) {
                                        vnode.attrs.onfocusout(e)
                                    }

                                    // Allow the onclick event handler to be called, before its cleaned up.
                                    setTimeout(() => (this.data.focussed = false), 100)
                                    if (vnode.attrs.search) {
                                        return
                                    }

                                    modelref_assign(model_ref, value)
                                }}
                                onfocus={() => {
                                    this.data.focussed = true
                                }}
                                oninput={(e: InputEvent) => {
                                    if (vnode.attrs.validation) {
                                        vnode.attrs.validation.dirty = true
                                    }

                                    // Type number/date are native formats for the input element.
                                    // Other types may use a custom formatter.
                                    let value: string | number | null = event_value(e)
                                    if (['date', 'number'].includes(vnode.attrs.type ?? '')) {
                                        value = this.format_value(vnode, value)
                                    } else if (vnode.attrs.formatter) {
                                        value = vnode.attrs.formatter(value)
                                    }

                                    // A null formatted value just means that the value is invalid
                                    // And must not be accepted as input.

                                    if (value === null && !['number', 'date'].includes(vnode.attrs.type ?? '')) {
                                        e.preventDefault()
                                        return
                                    }

                                    const valueStr = value != null ? String(value) : ''
                                    modelref_assign(model_ref, valueStr)

                                    if (vnode.attrs.onafterupdate) {
                                        vnode.attrs.onafterupdate(valueStr)
                                    }

                                    if (vnode.attrs.oninput) {
                                        vnode.attrs.oninput(valueStr)
                                    }
                                }}
                                placeholder={vnode.attrs.placeholder ? vnode.attrs.placeholder : ''}
                                min={'min' in vnode.attrs ? vnode.attrs.min : undefined}
                                max={'max' in vnode.attrs ? vnode.attrs.max : undefined}
                                step={(() => {
                                    if (vnode.attrs.type === 'number' && typeof vnode.attrs.decimals === 'number') {
                                        return `0.${'0'.repeat(vnode.attrs.decimals - 1)}1`
                                    }
                                    return vnode.attrs.step
                                })()}
                                tabindex={vnode.attrs.tabindex}
                                type={(() => {
                                    if ('type' in vnode.attrs) {
                                        if (vnode.attrs.type === 'password') {
                                            return this.data.password.hide ? 'password' : 'text'
                                        }
                                        if (vnode.attrs.type === 'currency') {
                                            return 'text'
                                        }
                                        return vnode.attrs.type
                                    }
                                    return 'text'
                                })()}
                                value={model_value}
                            />
                            {vnode.attrs.type === 'password' && (
                                <Icon
                                    name={this.data.password.hide ? 'eye' : 'eyeRemove'}
                                    onclick={() => {
                                        this.data.password.hide = !this.data.password.hide
                                    }}
                                />
                            )}
                            {vnode.children}

                            {vnode.attrs.search &&
                                (() => {
                                    const search = vnode.attrs.search
                                    return (
                                        <div
                                            className={classes('search-suggestions', {
                                                hidden: !this.data.focussed,
                                            })}
                                            onclick={(e: MouseEvent) => {
                                                const clicked_value =
                                                    e.target instanceof Element
                                                        ? e.target.closest<HTMLElement>('[data-id]')?.dataset.id
                                                        : undefined
                                                const suggestion_index = search.options.findIndex((i) => {
                                                    if (typeof i.value === 'string') {
                                                        return i.value === clicked_value
                                                    }
                                                    return i.value === Number(clicked_value)
                                                })
                                                const suggestion = search.options[suggestion_index]
                                                if (!suggestion) {
                                                    return
                                                }
                                                const {model_ref: suggestion_model_ref} = modelref_adapter(vnode.attrs.model)
                                                modelref_assign(suggestion_model_ref, suggestion.value)
                                                this.onsuggestion(vnode, suggestion)
                                                search.options.length = 0
                                            }}
                                            tabindex='-1'
                                        >
                                            {search.options.map((suggestion, index: number) => (
                                                <div
                                                    className={classes('suggestion', {
                                                        selected: index === this.data.search.selected,
                                                    })}
                                                    data-id={suggestion.value}
                                                >
                                                    {suggestion.label}
                                                </div>
                                            ))}
                                        </div>
                                    )
                                })()}

                            {(() => {
                                if (invalid && validation?.dirty) {
                                    return <div className='help validation'>{failure_message(invalid)}</div>
                                } else if (vnode.attrs.help) {
                                    return <div class='help'>{vnode.attrs.help}</div>
                                }
                                return undefined
                            })()}
                        </div>
                    )
                })()}
            </div>
        )
    }
}
